import { useMemo, useCallback } from "react";
import { useGetList, useUpdate, useCreate, useDelete, useNotify } from "react-admin";
import { useQueryClient } from "@tanstack/react-query";
import type {
  EndorsementListItem,
  EndorsementCategory,
  EndorsementType,
  QualifyingCondition,
  AdminChangeEntry,
  AmendmentEntry,
  TieredDeductibleTier,
  DomesticReimbursementEntry,
} from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

// ─────────────────────────────────────────────────────────────
// Category map (frontend-only — not in schema)
// ─────────────────────────────────────────────────────────────

export const ENDORSEMENT_CATEGORIES: EndorsementCategory[] = [
  {
    key: "deductible_modification",
    label: "Deductible Modification",
    types: ["deductible_modification", "family_deductible", "tiered_deductible"],
  },
  {
    key: "aggregate_accommodation",
    label: "Aggregate Accommodation",
    types: ["aggregate_accommodation"],
  },
  {
    key: "experience_rating",
    label: "Experience Rating",
    types: ["experience_credit", "experience_refund"],
  },
  {
    key: "rate_protection",
    label: "Rate Protection",
    types: ["rate_caps", "no_new_lasers"],
  },
  {
    key: "funding",
    label: "Funding",
    types: ["advance_funding"],
  },
  {
    key: "other",
    label: "Other Endorsements",
    types: [
      "admin_changes",
      "leave_of_absence",
      "amendments",
      "provider_dispute_credit",
      "plan_mirroring",
      "retained_corridor",
      "domestic_reimbursement_adjustments",
    ],
  },
  {
    key: "specialty_coverage",
    label: "Specialty & Coverage",
    types: [
      "specific_terminal_liability",
      "aggregate_terminal_liability",
      "contingent_terminal_liability",
      "clinical_trials",
      "transplant_coverage",
      "travel_medical",
      "reference_based_pricing",
      "specialty_drug",
      "exclusions",
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────

/**
 * Formats valid_period_start / valid_period_end into a display string.
 * Returns "—" if both are null (endorsement not configured).
 */
export const formatValidPeriod = (start: string | null, end: string | null): string => {
  if (!start && !end) return "—";
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-");
    return `${m}/${d}/${y}`;
  };
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return `${fmt(start)} – ongoing`;
  return `through ${fmt(end!)}`;
};

// ─────────────────────────────────────────────────────────────
// API ↔ Frontend transformation
// ─────────────────────────────────────────────────────────────

/** Junction array keys in config that should be hoisted to top-level EndorsementListItem fields */
const JUNCTION_KEYS = new Set([
  "codes",
  "qualifying_conditions",
  "tiers",
  "admin_change_entries",
  "amendment_entries",
  "trigger_types",
  "phases",
  "excluded_icd",
  "excluded_cpt",
  "ndc_codes",
  "excluded_countries",
  "excluded_facilities",
  "credited_claim_ids",
  "domestic_reimbursement_entries",
]);

/**
 * Derive `is_currently_active` from `is_enabled` + the valid period.
 * An endorsement is active when it is enabled AND either:
 * - The parent policy is active (skip date check — endorsements follow the policy), OR
 * - The current date falls within its valid period (if one is defined).
 */
const deriveIsCurrentlyActive = (raw: EndorsementListItem, policyIsActive = false): boolean => {
  if (!raw.is_enabled) return false;
  if (policyIsActive) return true;
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  if (raw.valid_period_start && today < raw.valid_period_start) return false;
  if (raw.valid_period_end && today > raw.valid_period_end) return false;
  return true;
};

/**
 * Transform API response → EndorsementListItem.
 * The backend embeds junction arrays inside the `config` JSON blob.
 * This function hoists them to top-level fields for UI consumption.
 * Also derives `is_currently_active` since the API doesn't return it.
 */
const transformFromApi = (raw: EndorsementListItem, policyIsActive = false): EndorsementListItem => {
  const rawConfig = (raw.config as Record<string, unknown>) ?? {};
  const scalarConfig: Record<string, unknown> = {};
  const junctionData: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(rawConfig)) {
    if (JUNCTION_KEYS.has(key)) {
      junctionData[key] = value;
    } else {
      scalarConfig[key] = value;
    }
  }

  return {
    ...raw,
    is_currently_active: deriveIsCurrentlyActive(raw, policyIsActive),
    config: Object.keys(scalarConfig).length > 0 ? scalarConfig : null,
    ...junctionData,
  } as EndorsementListItem;
};

/**
 * Reassemble full config for API updates by merging scalar config + junction arrays.
 * The backend expects all type-specific data inside `config`.
 */
/** Assemble final API payload: common fields + config (always included, even if empty `{}`) */
const buildPayload = (common: Record<string, unknown>, config: Record<string, unknown>): Record<string, unknown> => ({
  ...common,
  config,
});

const buildApiConfig = (endorsement: EndorsementListItem): Record<string, unknown> => {
  const config: Record<string, unknown> = {
    ...((endorsement.config as Record<string, unknown>) ?? {}),
  };

  for (const key of JUNCTION_KEYS) {
    const value = endorsement[key as keyof EndorsementListItem];
    if (value !== undefined) {
      config[key] = value;
    }
  }

  return config;
};

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

/**
 * Fetches endorsements for a policy from the API and provides:
 * - Category-based grouping and lookup helpers
 * - CRUD mutations (toggle active, update codes/conditions, create, update, delete)
 *
 * API: GET /policies/{policyId}/endorsements
 */
export const useEndorsements = (policyId: string, policyIsActive = false) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const queryClient = useQueryClient();
  const resource = `policies/${policyId}/endorsements`;

  /** Invalidate audit queries so Event History auto-refreshes after mutations */
  const invalidateAudit = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["audit"] });
  }, [queryClient]);

  // ── List ────────────────────────────────────────────────────

  const {
    data: rawEndorsements = [],
    isLoading,
    refetch,
  } = useGetList<EndorsementListItem>(
    resource,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "type", order: "ASC" },
    },
    { enabled: !!policyId }
  );

  // Transform API responses (hoist junction arrays from config)
  const endorsements = useMemo(
    () => rawEndorsements.map((e) => transformFromApi(e, policyIsActive)),
    [rawEndorsements, policyIsActive]
  );

  // ── Computed helpers ────────────────────────────────────────

  const getByCategory = useMemo(() => {
    const byType = new Map<EndorsementType, EndorsementListItem[]>();
    for (const e of endorsements) {
      const list = byType.get(e.type) || [];
      list.push(e);
      byType.set(e.type, list);
    }

    return (categoryKey: string): EndorsementListItem[] => {
      const category = ENDORSEMENT_CATEGORIES.find((c) => c.key === categoryKey);
      if (!category) return [];
      return category.types.flatMap((t) => byType.get(t) || []);
    };
  }, [endorsements]);

  const getById = useCallback(
    (endorsementId: string): EndorsementListItem | undefined => {
      return endorsements.find((e) => e.id === endorsementId);
    },
    [endorsements]
  );

  // ── Mutations ──────────────────────────────────────────────

  const [update] = useUpdate();
  const [create] = useCreate();
  const [remove] = useDelete();

  /** Helper: build the update payload for PUT. Optional auditAction for specific event labeling. */
  const buildUpdatePayload = useCallback(
    (endorsement: EndorsementListItem, overrides?: Partial<EndorsementListItem>, auditAction?: string) => {
      const merged = { ...endorsement, ...overrides } as EndorsementListItem;
      const common: Record<string, unknown> = {
        type: merged.type,
        is_enabled: merged.is_enabled,
        valid_period_start: merged.valid_period_start ?? "",
        valid_period_end: merged.valid_period_end ?? "",
      };
      if (auditAction) common.audit_action = auditAction;
      return buildPayload(common, buildApiConfig(merged));
    },
    []
  );

  /** Toggle active/inactive (Workflow 5) — PUT with flipped is_enabled */
  const toggleActive = useCallback(
    (endorsementId: string) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;
      const nowEnabled = !endorsement.is_enabled;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            endorsement,
            { is_enabled: nowEnabled },
            nowEnabled ? "update:activated" : "update:deactivated"
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            notify(`Endorsement ${nowEnabled ? "activated" : "deactivated"}`, { type: "success" });
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notify, notifyError, refetch, invalidateAudit]
  );

  /** Update ICD/CPT codes (Workflow 7) — PUT with modified config.codes */
  const updateCodes = useCallback(
    (endorsementId: string, codeType: "icd" | "cpt", codes: string[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;
      const updatedCodes = { ...endorsement.codes, [codeType]: codes };

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload({ ...endorsement, codes: updatedCodes }, undefined, "update:codes"),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Update qualifying conditions (Workflow 8) — PUT with modified config */
  const updateQualifyingConditions = useCallback(
    (endorsementId: string, conditions: QualifyingCondition[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            { ...endorsement, qualifying_conditions: conditions },
            undefined,
            "update:qualifying_conditions"
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Update admin change entries — PUT with modified config.admin_change_entries */
  const updateAdminChangeEntries = useCallback(
    (endorsementId: string, entries: AdminChangeEntry[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            { ...endorsement, admin_change_entries: entries },
            undefined,
            "update:admin_change_entries"
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Update amendment entries — PUT with modified config.amendment_entries */
  const updateAmendmentEntries = useCallback(
    (endorsementId: string, entries: AmendmentEntry[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            { ...endorsement, amendment_entries: entries },
            undefined,
            "update:amendment_entries"
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Update domestic reimbursement entries — PUT with modified config.domestic_reimbursement_entries */
  const updateDomesticReimbursementEntries = useCallback(
    (endorsementId: string, entries: DomesticReimbursementEntry[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            { ...endorsement, domestic_reimbursement_entries: entries },
            undefined,
            "update:domestic_reimbursement_entries"
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Generic update for any string-array junction field (excluded_icd, ndc_codes, trigger_types, etc.) */
  const updateStringArray = useCallback(
    (endorsementId: string, field: string, values: string[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload(
            { ...endorsement, [field]: values } as EndorsementListItem,
            undefined,
            `update:${field}`
          ),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Update tiered deductible tiers — PUT with modified config.tiers */
  const updateTiers = useCallback(
    (endorsementId: string, tiers: TieredDeductibleTier[]) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      if (!endorsement) return;

      void update(
        resource,
        {
          id: endorsementId,
          data: buildUpdatePayload({ ...endorsement, tiers }, undefined, "update:tiers"),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            void refetch();
            invalidateAudit();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, buildUpdatePayload, notifyError, refetch, invalidateAudit]
  );

  /** Create a new endorsement — POST with type + config */
  const createEndorsement = useCallback(
    (type: EndorsementType, data: Record<string, unknown>, onSuccess?: () => void) => {
      const { is_enabled, valid_period_start, valid_period_end, ...rest } = data;
      const common: Record<string, unknown> = {
        type,
        is_enabled: is_enabled ?? true,
        valid_period_start: valid_period_start ?? "",
        valid_period_end: valid_period_end ?? "",
      };
      void create(
        resource,
        {
          data: buildPayload(common, rest),
        },
        {
          onSuccess: () => {
            notify("Endorsement created successfully", { type: "success" });
            void refetch();
            invalidateAudit();
            onSuccess?.();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [resource, create, notify, notifyError, refetch, invalidateAudit]
  );

  /** Update an existing endorsement — PUT with updated config */
  const updateEndorsement = useCallback(
    (endorsementId: string, data: Record<string, unknown>, onSuccess?: () => void) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      const { is_enabled, valid_period_start, valid_period_end, ...rest } = data;
      const common: Record<string, unknown> = {
        type: endorsement?.type,
        is_enabled: is_enabled ?? true,
        valid_period_start: valid_period_start ?? "",
        valid_period_end: valid_period_end ?? "",
        audit_action: "update:configuration",
      };

      void update(
        resource,
        {
          id: endorsementId,
          data: buildPayload(common, rest),
          previousData: endorsement,
        },
        {
          onSuccess: () => {
            notify("Endorsement updated successfully", { type: "success" });
            void refetch();
            invalidateAudit();
            onSuccess?.();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, update, notify, notifyError, refetch, invalidateAudit]
  );

  /** Delete an endorsement — DELETE */
  const deleteEndorsement = useCallback(
    (endorsementId: string, onSuccess?: () => void) => {
      const endorsement = endorsements.find((e) => e.id === endorsementId);
      void remove(
        resource,
        { id: endorsementId, previousData: endorsement },
        {
          onSuccess: () => {
            notify("Endorsement deleted", { type: "success" });
            void refetch();
            invalidateAudit();
            onSuccess?.();
          },
          onError: (error) => notifyError(error),
        }
      );
    },
    [endorsements, resource, remove, notify, notifyError, refetch, invalidateAudit]
  );

  // ── Return ─────────────────────────────────────────────────

  const activeCount = useMemo(() => endorsements.filter((e) => e.is_currently_active).length, [endorsements]);

  return {
    endorsements,
    isLoading,
    getByCategory,
    getById,
    activeCount,
    categories: ENDORSEMENT_CATEGORIES,
    refetch,
    // Mutations
    toggleActive,
    updateCodes,
    updateQualifyingConditions,
    updateAdminChangeEntries,
    updateAmendmentEntries,
    updateDomesticReimbursementEntries,
    updateTiers,
    updateStringArray,
    createEndorsement,
    updateEndorsement,
    deleteEndorsement,
  };
};
